import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Tabs } from "../../../components/ui";
import SideDrawer from "../../../components/staff-ops/SideDrawer";
import PersonAvatar from "../../../components/staff-ops/PersonAvatar";
import AttendanceHeatmap, { heatmapKey } from "../../../components/staff-ops/AttendanceHeatmap";
import { relativeFrom, tenure } from "../../../components/staff-ops/relative";
import { fetchAllAttendances, fetchPayments } from "../../payroll/services/payrollService";
import { attendanceStatusLabel } from "../../payroll/theme";
import type { AttendanceRecord, PayrollPayment } from "../../payroll/types";
import { fetchActivities, type ActivityRow } from "../../staff/services/activitiesService";
import { STATUS_TONE } from "../../staff/lib/format";
import { formatMoney, modeLabel, roleLabel, ROLE_TONES, TEAM_MEMBER_STATUS_TONES, teamMemberStatusLabel } from "../theme";
import type { Row } from "./ApiCrudSection";

// ---------------------------------------------------------------------------
// Ficha lateral de una persona del directorio (CC-CONSOLE#1595): perfil,
// asistencia de los últimos 30 días, actividades asignadas y pagos. Cada
// pestaña pide lo suyo al abrirse; el servidor sigue decidiendo qué ve cada
// quien (quien no administra recibe 403 o solo lo propio, y se dice).
// ---------------------------------------------------------------------------

type TabId = "profile" | "attendance" | "activities" | "payments";

interface Props {
  member: Row | null;
  onClose: () => void;
  /** Sueldo, tarifas y pagos: solo quien administra Staff. */
  canSeePay: boolean;
  onEdit?: (member: Row) => void;
}

const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

function useLoad<T>(enabled: boolean, key: string, loader: () => Promise<T>) {
  const [state, setState] = useState<{ key: string; data: T | null; error: boolean; loading: boolean }>({ key: "", data: null, error: false, loading: false });
  useEffect(() => {
    if (!enabled || state.key === key) return;
    let alive = true;
    setState({ key, data: null, error: false, loading: true });
    loader()
      .then((data) => alive && setState({ key, data, error: false, loading: false }))
      .catch(() => alive && setState({ key, data: null, error: true, loading: false }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);
  return state.key === key ? state : { key, data: null, error: false, loading: enabled };
}

export default function MemberDetailDrawer({ member, onClose, canSeePay, onEdit }: Props) {
  const { t, i18n } = useTranslation("common");
  const [tab, setTab] = useState<TabId>("profile");
  const id = member?.id ?? "";
  useEffect(() => setTab("profile"), [id]);

  const to = isoDay(Date.now());
  const from = isoDay(Date.now() - 29 * 86400000);
  const attendance = useLoad<AttendanceRecord[]>(Boolean(member) && tab === "attendance", `att|${id}`, async () => (await fetchAllAttendances({ from, to, teamMemberId: id })).items.filter((r) => r.teamMemberId === id));
  const activities = useLoad<ActivityRow[]>(Boolean(member) && tab === "activities", `act|${id}`, async () => (await fetchActivities({ assigneeTeamMemberId: id, includeDone: true, limit: 200 })).data);
  const payments = useLoad<PayrollPayment[]>(Boolean(member) && tab === "payments", `pay|${id}`, async () => (await fetchPayments({ teamMemberId: id })).items.filter((p) => p.teamMemberId === id));

  const attCounts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of attendance.data ?? []) {
      const k = heatmapKey(r);
      out[k] = (out[k] ?? 0) + 1;
    }
    return out;
  }, [attendance.data]);

  if (!member) return null;
  const name = String(member.fullName || [member.name, member.lastName].filter(Boolean).join(" ") || member.id);
  const status = String(member.status || "active");
  const ten = tenure(member.hireDate as string | null);
  const stateLabel = (k: string) => (k === "late" ? t("staffOps.heatmap.late") : attendanceStatusLabel(t, k));

  const tabs = [
    { id: "profile", label: t("staffOps.drawer.tabs.profile"), icon: "ri-user-3-line" },
    { id: "attendance", label: t("staffOps.drawer.tabs.attendance"), icon: "ri-calendar-check-line" },
    { id: "activities", label: t("staffOps.drawer.tabs.activities"), icon: "ri-task-line" },
    ...(canSeePay ? [{ id: "payments", label: t("staffOps.drawer.tabs.payments"), icon: "ri-wallet-3-line" }] : []),
  ];

  const field = (label: string, value: ReactNode) => (
    <div className="rounded-lg border border-line bg-card px-3 py-2">
      <dt className="text-[11px] uppercase tracking-wide text-content-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-content">{value ?? "—"}</dd>
    </div>
  );
  const loadingEl = (
    <p className="py-8 text-center text-sm text-content-secondary">
      <i className="ri-loader-4-line mr-1 animate-spin" aria-hidden="true" />
      {t("team.loading")}
    </p>
  );
  const errorEl = (
    <p className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error" role="alert">
      {t("staffOps.drawer.loadError")}
    </p>
  );
  const emptyEl = (key: string) => <p className="py-8 text-center text-sm text-content-secondary">{t(key)}</p>;

  return (
    <SideDrawer
      open
      onClose={onClose}
      closeLabel={t("staffOps.drawer.close")}
      testId="member-drawer"
      leading={<PersonAvatar name={name} color={member.avatarColor as string | null} size="lg" presence={status === "active" ? "online" : status === "inactive" ? "away" : "offline"} />}
      title={name}
      subtitle={[member.jobTitle, member.area].filter(Boolean).join(" · ") || roleLabel(t, member.role)}
      meta={
        <>
          <span className={`rounded-full px-2 py-0.5 text-[11px] ${TEAM_MEMBER_STATUS_TONES[status] ?? "bg-subtle text-content-secondary"}`}>{teamMemberStatusLabel(t, status)}</span>
          <span className={`rounded-full px-2 py-0.5 text-[11px] ${ROLE_TONES[String(member.role)] ?? "bg-subtle text-content-secondary"}`}>{roleLabel(t, member.role)}</span>
          {member.employmentType ? (
            <span className="rounded-full bg-subtle px-2 py-0.5 text-[11px] text-content-secondary">{t(`staffOps.employmentType.${String(member.employmentType)}`)}</span>
          ) : null}
          {member.squadName ? (
            <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[11px] text-violet-500">
              <i className="ri-group-2-line mr-1" aria-hidden="true" />
              {String(member.squadName)}
            </span>
          ) : null}
          {onEdit && (
            <button type="button" onClick={() => onEdit(member)} className="ml-auto rounded-lg border border-line px-2 py-1 text-xs text-content hover:bg-subtle" data-testid="member-drawer-edit">
              <i className="ri-edit-line mr-1" aria-hidden="true" />
              {t("team.edit")}
            </button>
          )}
        </>
      }
      toolbar={<Tabs items={tabs} activeId={tab} onChange={(v) => setTab(v as TabId)} />}
    >
      {tab === "profile" && (
        <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="member-profile">
          {field(t("team.fields.email"), member.email ? <a className="text-primary-500 hover:underline" href={`mailto:${String(member.email)}`}>{String(member.email)}</a> : null)}
          {field(t("team.fields.phone"), member.phone ? String(member.phone) : null)}
          {field(t("staffOps.fields.area"), member.area ? String(member.area) : null)}
          {field(t("staffOps.fields.squad"), member.squadName ? String(member.squadName) : null)}
          {field(t("staffOps.fields.location"), member.locationName ? String(member.locationName) : null)}
          {field(
            t("team.fields.hireDate"),
            member.hireDate ? (
              <>
                {String(member.hireDate)}
                {ten && <span className="ml-1 text-xs text-content-secondary">· {t("staffOps.drawer.tenure", { years: ten.years, months: ten.months })}</span>}
              </>
            ) : null,
          )}
          {field(t("team.fields.payMode"), modeLabel(t, member.payMode ?? "daily"))}
          {field(t("team.fields.weeklyCapacityHours"), member.weeklyCapacityHours ? `${String(member.weeklyCapacityHours)} h` : null)}
          {canSeePay && field(t("team.fields.payRate"), member.payRateName ? String(member.payRateName) : null)}
          {canSeePay &&
            field(
              t("staffOps.fields.rate"),
              member.payMode === "hourly"
                ? `${formatMoney(member.hourlyRate, String(member.currency ?? "MXN"))} / h`
                : member.payMode === "fixed_salary"
                  ? `${formatMoney(member.fixedSalary, String(member.currency ?? "MXN"))} / ${t("staffOps.drawer.period")}`
                  : `${formatMoney(member.dailyRate, String(member.currency ?? "MXN"))} / ${t("staffOps.drawer.day")}`,
            )}
          <div className="sm:col-span-2">
            {field(
              t("team.fields.allocations"),
              Array.isArray(member.projectAllocations) && member.projectAllocations.length ? (
                <ul className="space-y-1">
                  {(member.projectAllocations as { projectId: string; projectName: string; percentage: number }[]).map((a) => (
                    <li key={a.projectId} className="flex items-center gap-2">
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-subtle">
                        <span className="block h-full rounded-full bg-primary-500" style={{ width: `${Math.min(100, Number(a.percentage) || 0)}%` }} />
                      </span>
                      <span className="w-48 truncate text-xs">
                        {a.percentage}% {a.projectName}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null,
            )}
          </div>
          {member.notes ? <div className="sm:col-span-2">{field(t("team.fields.notes"), String(member.notes))}</div> : null}
        </dl>
      )}

      {tab === "attendance" &&
        (attendance.loading ? (
          loadingEl
        ) : attendance.error ? (
          errorEl
        ) : (
          <div className="space-y-3" data-testid="member-attendance">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {["present", "late", "absent", "sick_leave"].map((k) => (
                <div key={k} className="rounded-lg border border-line bg-card px-3 py-2">
                  <div className="text-[11px] text-content-muted">{stateLabel(k)}</div>
                  <div className="text-lg font-semibold text-content">{attCounts[k] ?? 0}</div>
                </div>
              ))}
            </div>
            <AttendanceHeatmap
              records={attendance.data ?? []}
              from={from}
              to={to}
              labelOf={stateLabel}
              title={t("staffOps.drawer.last30")}
              emptyLabel={t("staffOps.heatmap.empty")}
              overtimeLabel={t("staffOps.heatmap.overtime")}
            />
          </div>
        ))}

      {tab === "activities" &&
        (activities.loading ? (
          loadingEl
        ) : activities.error ? (
          errorEl
        ) : !(activities.data ?? []).length ? (
          emptyEl("staffOps.drawer.noActivities")
        ) : (
          <ul className="space-y-2" data-testid="member-activities">
            {(activities.data ?? []).slice(0, 40).map((a) => (
              <li key={a.id} className="flex items-center gap-3 rounded-lg border border-line bg-card px-3 py-2">
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_TONE[a.status] ?? ""}`}>{t(`staffActivities.status.${a.status}`)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-content">{a.title}</span>
                  <span className="text-[11px] text-content-muted">
                    {a.key}
                    {a.storyPoints != null ? ` · ${a.storyPoints} ${t("staffActivities.card.points")}` : ""}
                    {a.statusChangedAt ? ` · ${relativeFrom(a.statusChangedAt, i18n.language) ?? ""}` : ""}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ))}

      {tab === "payments" &&
        (payments.loading ? (
          loadingEl
        ) : payments.error ? (
          errorEl
        ) : !(payments.data ?? []).length ? (
          emptyEl("staffOps.drawer.noPayments")
        ) : (
          <ul className="space-y-2" data-testid="member-payments">
            {[...(payments.data ?? [])]
              .sort((a, b) => b.paidAt.localeCompare(a.paidAt))
              .map((p) => (
                <li key={p.id} className="flex items-center gap-3 rounded-lg border border-line bg-card px-3 py-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success/15 text-success">
                    <i className={p.kind === "salary" ? "ri-wallet-3-line" : "ri-gift-line"} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-content">{p.period}</span>
                    <span className="text-[11px] text-content-muted">
                      {p.paidAt} · {relativeFrom(p.paidAt, i18n.language)} · {String(p.method).toUpperCase()}
                    </span>
                  </span>
                  <span className="text-sm font-semibold text-content">{formatMoney(p.amount, p.currency)}</span>
                </li>
              ))}
          </ul>
        ))}
    </SideDrawer>
  );
}
